
import MessageRow from "../com/message-row.vue";
import Sku from "../com/sku.vue";
import SkuDetail from "../com/sku-detail.vue";
import ComGroup from "../com/group.vue";
import ComPostage from "../com/postage.vue";
import SendDialog from "../com/send-dialog.vue";
import { getPaidLevelList } from "@/common/vip";
import { getCouponList } from "@/common/coupon";
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator'

@Component({
  components: {
    MessageRow,
    Sku,
    SkuDetail,
    ComGroup,
    ComPostage,
    SendDialog,
  }
})
export default class extends Vue {
  @Prop()
  formData: any

  @Prop()
  submitLoading: any

  @Prop()
  loading: any

  /** 付费会员等级选项，只给「发货方式 = 会员」用 */
  levelList: any[] = [];
  /** couponId -> 券名，发货方式显示已选券用 */
  couponMap: Record<string, string> = {};
  /** 两个列表都只拉一次，切来切去不重复请求 */
  private _sendListLoaded = false;

  goodsType = [
    {
      name: "实物商品",
      desc: "(物流发货)",
      value: 1,
    },
    {
      name: "虚拟商品",
      desc: "(无需物流)",
      value: 2,
    },
  ]
  /**
   * 编辑已有商品：clone 也带 id，但提交时会把 id 删掉当新建，
   * 所以只有「有 id 且不是 clone」才算真编辑
   */
  get isEdit() {
    return !!this.formData?.id && this.$route.query.type != 'clone';
  }

  /**
   * 规格选项：已有 SKU 用它的 id，本次新建的用 `new-下标`。
   * 发货配置挂的是 SKU，而新建商品的 SKU 要等商品存完才有 id，所以两种值都得能表达。
   */
  get skuOptions() {
    return (this.formData?.skuList || []).map((item, index) => ({
      value: item.id ? item.id : `new-${index}`,
      label: this.skuLabel(index),
    }));
  }

  /**
   * 规格组合的展示名，口径和 sku-detail 的 computSku 一致：
   * 跳过没填值的规格，最后一个规格变化最快（后端 sort: i 就是按这个顺序写的）。
   *
   * 不复用 $getSpecList：它不过滤空规格，只要有一个规格没填值就整列返回 null。
   */
  skuLabel(index) {
    let specs = (this.formData?.spec || []).filter((item) => item.specList && item.specList.length);
    if (!specs.length) return `规格${index + 1}`;
    let names: string[] = [];
    let cursor = index;
    for (let i = specs.length - 1; i >= 0; i--) {
      let specList = specs[i].specList;
      names.unshift((specList[cursor % specList.length] || {}).name || "");
      cursor = Math.floor(cursor / specList.length);
    }
    let label = names.filter((name) => !!name).join(" / ");
    return label || `规格${index + 1}`;
  }

  /**
   * 配了卡密发货的规格下标：那些规格的库存由卡密池维护，后端 stock-operat 会直接拒
   * （'该规格的库存由卡密导入/作废维护，请勿手工调整'），所以库存列得锁上。
   * 按规格锁而不是整表锁：同一个商品可能只有某个规格发卡密。
   */
  get cardSendIndexes() {
    if (this.formData?.type != 2) return [];
    let options = this.skuOptions;
    let indexes: number[] = [];
    (this.formData?.virtualSendList || []).forEach((row) => {
      if (row.type != 1) return;
      let index = options.findIndex((item) => item.value == row.skuValue);
      if (index > -1) indexes.push(index);
    });
    return indexes;
  }

  created() {
    if (this.formData?.type == 2) this.ensureSendList();
  }

  /**
   * 只有虚拟商品需要这两份下拉数据，实物商品不拉。
   *
   * 切回实物时清空发货配置：残留的行仍会被后端校验（GoodsOperaVirtualDto 是独立的
   * @Body()，和 payload.type 无关），留着会让保存报「请选择要发放的优惠券」，
   * 而用户根本没看到那个字段。
   */
  @Watch('formData.type')
  typeWatch(val) {
    if (val == 2) {
      this.ensureSendList();
      return;
    }
    this.formData.virtualSendList = [];
  }

  async ensureSendList() {
    if (this._sendListLoaded) return;
    let [levelRes, couponRes] = await Promise.all([getPaidLevelList(), getCouponList()]);
    /** 失败不置标记，下次切回来还能重试 */
    if (levelRes.error || couponRes.error) return;
    this.levelList = levelRes.data?.list || [];
    let map: Record<string, string> = {};
    (couponRes.data?.list || []).forEach((item) => {
      map[item.id] = item.name;
    });
    this.couponMap = map;
    this._sendListLoaded = true;
  }

  /** 每个规格的礼物摘要，下标和 formData.skuList 对齐，给规格明细的礼物列显示 */
  get giftTexts() {
    let typeNames = { 1: "卡密", 2: "优惠券", 3: "会员", 4: "接口通知" };
    return this.skuOptions.map((option) => {
      return (this.formData?.virtualSendList || [])
        .filter((item) => item.skuValue == option.value)
        .map((item) => typeNames[item.type] || "未知")
        .join(" + ");
    });
  }

  /**
   * 点某个规格的礼物栏：把属于它的行拷进弹窗改，确定后用返回的整组替换。
   *
   * 必须拷副本 —— 点取消时外面那份配置不能被改到。
   * 保留 id 的行是更新、不在返回数组里的行后端会删掉，所以「整组替换」是安全的，
   * 但只用它替换**这个规格**的行，别动别的规格。
   */
  async editGift(index) {
    let option = this.skuOptions[index];
    if (!option) return;
    let rows = (this.formData.virtualSendList || [])
      .filter((item) => item.skuValue == option.value)
      .map((item) => ({ ...item }));
    let result: any;
    try {
      result = await this.$dialog({
        title: `配置礼物（${option.label}）`,
        props: {
          width: "760px",
        },
        content: () => {
          return <SendDialog rows={rows} skuValue={option.value} levelList={this.levelList}
            couponMap={this.couponMap}></SendDialog>;
        },
      });
    } catch (e) {
      // 用户取消
      return;
    }
    /** 用 != 和上面挑行时的 == 保持一致：两边判断必须互补，否则同一行会既被留下又被加回来 */
    let kept = (this.formData.virtualSendList || []).filter((item) => item.skuValue != option.value);
    this.formData.virtualSendList = kept.concat(Array.isArray(result) ? result : []);
  }

  changeExpressType(val, bool) {
    if (bool) {
      this.formData.expressType = this.formData.expressType | val;
    } else {
      this.formData.expressType = this.formData.expressType ^ val;
    }
  }
  async submit() {
    this.$emit("submit");
  }
  addRow() {
    this.formData.message.push({
      name: `留言${this.formData.message.length + 1}`,
      type: "text",
      placeholder: "",
      isRequired: 0,
    });
  }
  processVideo(url) {
    return `${url}?x-oss-process=video/snapshot,t_0,m_fast,w_240`;
  }
}
